import { useState, type KeyboardEvent } from 'react'
import { Segmented } from '../../ui'
import { hm, type AdminUsageDay, type AdminUserOverview } from './model'

/*
 * Hours used per day (DailyUsage, 03-data-viz): one chart per metric, both chart-1, stacked on
 * one time axis, day labels under the last chart only; every day on the axis (a day with
 * nothing is zero); bars radius-sm on the top end, 2 px gap, base on chart-axis; y axis in whole
 * hours; no limit line (allowances are monthly — the meter shows them). Hover or focus a day:
 * a dashed line-strong guide across both charts, the other days at 45 %, one tooltip with both
 * values; ← → move the day. "Table" shows the same columns, one row per day. Nothing animates.
 */

const W = 1000
const H = 96
const LEFT = 40
type Key = 'stream_hours_month' | 'captions_hours_month'
const METRICS: { k: Key; label: string }[] = [
  { k: 'stream_hours_month', label: 'Stream hours' },
  { k: 'captions_hours_month', label: 'Captions' },
]

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const dayLabel = (d: string) => `${Number(d.slice(8, 10))} ${MONTHS[Number(d.slice(5, 7)) - 1]}`

export function DailyUsage({ u, days, onDays }: { u: AdminUserOverview; days: 30 | 90; onDays: (d: 30 | 90) => void }) {
  const [view, setView] = useState<'chart' | 'table'>('chart')
  const [at, setAt] = useState<number | null>(null)
  const daily = u.usage_daily
  if (!daily) {
    return <p className="text-ui-body-sm text-muted">Usage by day needs the billing.read permission and billing on.</p>
  }
  const items = daily.items
  const total = (k: Key) => items.reduce((s, d) => s + d[k], 0)
  const key = (e: KeyboardEvent) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0
    if (!d) return
    e.preventDefault()
    setAt((x) => Math.min(items.length - 1, Math.max(0, (x ?? items.length - 1) + d)))
  }
  return (
    <section className="sx-panel flex flex-col gap-2" data-testid="daily-usage">
      <div className="flex flex-wrap items-center gap-3">
        <span className="sx-label">Usage by day · last {days} days</span>
        <span className="sx-mono text-ui-caption text-muted">
          Stream {hm(total('stream_hours_month'))} · Captions {hm(total('captions_hours_month'))}
        </span>
        <span className="flex-1" />
        <Segmented label="Range" value={String(days) as '30' | '90'} onChange={(v) => onDays(Number(v) as 30 | 90)}
          options={[{ value: '30', label: '30 days', testId: 'days-30' }, { value: '90', label: '90 days', testId: 'days-90' }]} />
        <Segmented label="View" value={view} onChange={setView}
          options={[{ value: 'chart', label: 'Chart', testId: 'view-chart' }, { value: 'table', label: 'Table', testId: 'view-table' }]} />
      </div>
      {view === 'table' ? (
        <table className="sx-table compact" data-testid="daily-table">
          <thead>
            <tr>
              <th>Day (UTC)</th>
              <th className="num">Stream hours</th>
              <th className="num">Captions</th>
            </tr>
          </thead>
          <tbody>
            {[...items].reverse().map((d) => (
              <tr key={d.day}>
                <td className="sx-mono">{d.day}</td>
                <td className="num tabular">{hm(d.stream_hours_month)}</td>
                <td className="num tabular">{hm(d.captions_hours_month)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="relative" tabIndex={0} role="group" aria-label="Usage by day; arrows move the day" onKeyDown={key}
          onBlur={() => setAt(null)} onMouseLeave={() => setAt(null)} data-testid="daily-chart">
          {METRICS.map((m, i) => (
            <div key={m.k}>
              <div className="mt-2 text-ui-body-sm font-medium text-text">{m.label}</div>
              <Bars items={items} k={m.k} at={at} onAt={setAt} labels={i === METRICS.length - 1} />
            </div>
          ))}
          {at !== null && items[at] && <Tip d={items[at]} at={at} n={items.length} />}
        </div>
      )}
    </section>
  )
}

/** Whole-hour scale: the top tick is the next whole hour over the highest day (at least 1 h). */
function scale(items: AdminUsageDay[], k: Key): number {
  const max = Math.max(0, ...items.map((d) => d[k])) / 3600
  return Math.max(1, Math.ceil(max))
}

function Bars({ items, k, at, onAt, labels }: { items: AdminUsageDay[]; k: Key; at: number | null; onAt: (i: number) => void; labels: boolean }) {
  const top = scale(items, k)
  const step = top <= 6 ? 1 : Math.ceil(top / 4)
  const ticks: number[] = []
  for (let v = 0; v <= top; v += step) ticks.push(v)
  const bw = (W - LEFT) / items.length
  const y = (h: number) => 4 + H - (h / top) * H
  const every = items.length > 31 ? 14 : 7
  return (
    <svg viewBox={`0 0 ${W} ${H + (labels ? 22 : 8)}`} className="block h-auto w-full" aria-hidden
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect()
        const x = ((e.clientX - r.left) / r.width) * W - LEFT
        if (x >= 0) onAt(Math.min(items.length - 1, Math.floor(x / bw)))
      }}>
      {ticks.map((v) => (
        <g key={v}>
          <line x1={LEFT} x2={W} y1={y(v)} y2={y(v)} stroke="var(--chart-grid)" strokeWidth={1} />
          <text x={LEFT - 6} y={y(v) + 4} textAnchor="end" className="fill-muted font-mono text-[11px]">{v} h</text>
        </g>
      ))}
      {items.map((d, i) => {
        const h = d[k] / 3600
        if (h <= 0) return null
        const x = LEFT + i * bw + 1
        const w = Math.max(1, bw - 2)
        const t = y(h)
        const r = Math.min(2, (h / top) * H, w / 2)
        return (
          <path key={d.day} d={`M${x} ${4 + H}V${t + r}Q${x} ${t} ${x + r} ${t}H${x + w - r}Q${x + w} ${t} ${x + w} ${t + r}V${4 + H}Z`}
            fill="var(--chart-1)" opacity={at === null || at === i ? 1 : 0.45} />
        )
      })}
      <line x1={LEFT} x2={W} y1={4 + H} y2={4 + H} stroke="var(--chart-axis)" />
      {at !== null && <line x1={LEFT + at * bw + bw / 2} x2={LEFT + at * bw + bw / 2} y1={4} y2={4 + H} stroke="var(--line-strong)" strokeDasharray="3 3" />}
      {labels && items.map((d, i) => (i % every === 0 ? (
        <text key={d.day} x={LEFT + i * bw + bw / 2} y={H + 20} textAnchor="middle" className="fill-muted font-mono text-[11px]">{dayLabel(d.day)}</text>
      ) : null))}
    </svg>
  )
}

function Tip({ d, at, n }: { d: AdminUsageDay; at: number; n: number }) {
  const left = `${Math.min(80, Math.max(4, (LEFT / W + (at + 0.5) * ((W - LEFT) / W / n)) * 100))}%`
  return (
    <div role="status" data-testid="daily-tip"
      className="pointer-events-none absolute top-6 z-10 -translate-x-1/2 rounded-md border border-line-2 bg-panel px-2.5 py-1.5 text-ui-caption shadow-(--shadow-overlay)"
      style={{ left }}>
      <div className="sx-mono text-text">{d.day}</div>
      <div className="text-text-soft">Stream hours <span className="sx-mono text-text">{hm(d.stream_hours_month)}</span></div>
      <div className="text-text-soft">Captions <span className="sx-mono text-text">{hm(d.captions_hours_month)}</span></div>
    </div>
  )
}
